<script setup>
import { onMounted, reactive, computed, ref } from 'vue';
import { TheChessboard } from 'vue3-chessboard';
import moveSoundSrc from '@/assets/move-self.mp3';
import 'vue3-chessboard/style.css';
import { usePuzzleStore } from '@/stores/puzzleStore';
import { Engine } from '@/composables/useEngine';
import blunderIcon from '@/assets/blunder.svg';
import correctIcon from '@/assets/correct.svg';
import bestIcon from '@/assets/best.svg';
import succSound from '@/assets/success.mp3';
import blunSound from '@/assets/wrong.mp3';

const puzzleStore = usePuzzleStore();
const currentBlunder = computed(() => puzzleStore.currentBlunder);

const blunderData = reactive({
  playing_side: '',
  lichessURL: '',
  mistake: '',
  best_move: '',
  opening: '',
});

let boardAPI = null;
let engine = null;

let boardEval = ref(null);

// For move sound
const moveSound = new Audio(moveSoundSrc);
const successSound = new Audio(succSound);
const blunderSound = new Audio(blunSound);

function playMoveSound() {
    moveSound.currentTime = 0;
    moveSound.play().catch((error) => {
        console.error('Unable to play move sound:', error);
    });
}

function playSuccessSound() {
  successSound.currentTime = 0;
  successSound.play().catch((error) => {
    console.error('Unable to play success sound:', error);
  });
}

function playBlunderSound() {
  blunderSound.currentTime = 0;
  blunderSound.volume = 0.5;
  blunderSound.play().catch((error) => {
    console.error('Unable to play blunder sound:', error);
  });
}

// Board configuration
const boardConfig = reactive({
  fen: '',
  orientation: '',
  turnColor: '',
  coordinates: true,
  drawable: {
    enabled: true,
    visible: true,
    defaultSnapToValidMove: true,
    eraseOnClick: true,
    shapes: [],
    autoShapes: [],
  },
});

/**
 * Called once the chessboard is created.
 * We create our engine instance here and store the board API reference.
 */
function handleBoardCreated(api) {
  boardAPI = api;
  // Initialize the Stockfish engine
  engine = new Engine(boardAPI);
}


async function checkEvaluation(fen) {
    let evaluation;
    try {
      evaluation = await engine.evaluatePosition(fen);
    } catch (e) {
      console.error('Engine error evaluating position:', e);
    }
    return evaluation;
}

/**
 * This runs after a player (or puzzle) makes a move on the board.
 * We then ask the engine to evaluate that move.
 */
async function handleMove(move) {
    playMoveSound();

    if (!userInteracted.value) {
       return;
    }

  // Get the new position in FEN
    if (!boardEval.value) {
        boardEval.value = await checkEvaluation(currentBlunder.value.fen);
        console.log('Initial evaluation from engine:', boardEval.value);
    }
    
    const fenAfterMove = boardAPI.getFen();

    // Evaluate the new position
    const evaluation = -(await checkEvaluation(fenAfterMove));

    console.log('Final evaluation from engine:', evaluation);

    // If the engine gave us no numeric eval, fallback to something
    if (evaluation == null) {
        console.log('Engine failed to evaluate the position.');
        return false;
    }

    // Decide if it’s a blunder
    // You might treat large positive values as good for White,
    // large negative values as good for Black, or just check the absolute value, etc.
    // For example, if the puzzle is from White's perspective:
    const moveText = move.from + move.to;

    if (evaluation >= (boardEval.value - 100)) {
      playSuccessSound();
      if (moveText === currentBlunder.value.best_move) {
        moveFeedback.visible = true;
        moveFeedback.type = 'best';
        moveFeedback.moveText = moveText;
      }
      else {
        moveFeedback.visible = true;
        moveFeedback.type = 'good';
        moveFeedback.moveText = moveText;
      }
      return true;
    } else {
        playBlunderSound(); 
        moveFeedback.visible = true;
        moveFeedback.type = 'blunder';
        moveFeedback.moveText = moveText;
        // Undo the last move so the user can retry
        setTimeout(() => {
        boardAPI.undoLastMove();
        }, 250);
        return false;
    }
}

// Load puzzle details into the board
async function loadPuzzle() {

    userInteracted.value = true;

    boardEval.value = null;

    if (!currentBlunder.value) return;

    console.log('Loading puzzle:', currentBlunder.value);

    const playSide = currentBlunder.value.playing_side ? 'white' : 'black';
    boardAPI?.resetBoard();
    boardConfig.viewOnly = false;
    boardConfig.orientation = playSide;
    boardConfig.turnColor = playSide;

    console.log(playSide);

    if (boardAPI) {
        boardAPI.setPosition(currentBlunder.value.fen);
    }

}

var userInteracted = ref(false);

function loadBlunder() {
    ////////////////////////////////
    blunderData.mistake = currentBlunder.value.mistake;
    blunderData.opening = currentBlunder.value.opening;
    blunderData.playing_side = currentBlunder.value.playing_side ? 'WHITE' : 'BLACK';
    blunderData.lichessURL = currentBlunder.value.liches_url;
    ////////////////////////////////

    console.log('playing_side', blunderData.playing_side);
    console.log('playingSide', currentBlunder.value.playing_side);

    moveFeedback.visible = true;
    moveFeedback.type = 'blunder';
    moveFeedback.moveText = blunderData.mistake;

    boardConfig.viewOnly = true;
    boardConfig.orientation = blunderData.playing_side ? 'white' : 'black';
    boardConfig.turnColor = blunderData.playing_side ? 'white' : 'black';

    userInteracted.value = false;
    if (boardAPI) {
        boardAPI.setPosition(currentBlunder.value.fen);
    }
   const move = currentBlunder.value.mistake;
    setTimeout(() => {
        const from = move.substring(0, 2);
        const to = move.substring(2, 4);
        boardAPI.move({ from, to });
    }, 500);
    
}

onMounted(() => {
    loadBlunder();
});

function nextBlunder() {
    const hasMoreBlunders = puzzleStore.nextBlunder();
    if (!hasMoreBlunders) {
        alert("No more opening blunders left!");
    } else {
        loadBlunder();
    }
}


function showBestMove() {
    if (boardAPI) {
        boardAPI.setPosition(currentBlunder.value.fen);
    }
    const bestMove = currentBlunder.value.best_move;
    const from = bestMove.substring(0, 2);
    const to = bestMove.substring(2, 4);
    boardAPI.move({ from, to });
}


const moveFeedback = reactive({
  visible: true,
  type: '',       // 'blunder' or 'good'
  moveText: '',   // e.g. "e4", "Nf3", etc.
});

function handleRetry() {
  // Hide any old feedback
  moveFeedback.visible = false
  moveFeedback.type = ''
  moveFeedback.moveText = ''

  // Then reset the board so user can try again
  loadPuzzle()
}


function handleBestMove() {
  moveFeedback.visible = true
  moveFeedback.type = 'best'
  moveFeedback.moveText = currentBlunder.value.best_move
  
  showBestMove()
}

function openLichessPosition() {
  window.open(blunderData.lichessURL, '_blank');
}


</script>

<template>
  <div class="d-flex flex-column vh-100 primary-font">
    <div class="d-flex flex-column justify-content-center align-items-center">
      <div class="d-flex flex-grow-1 justify-content-center align-items-center">
        <div class="d-flex flex-column vh-100">
          <!-- NAVBAR-->
          <div>
            <nav class="navbar-container d-flex align-items-center justify-content-between pt-3 pb-3">
              <span class="nav-item left search m-0" @click="$router.push({ name: 'search' })">&lt; NEW SEARCH</span>
            </nav>  
          </div>

          <div class="d-flex flex-column w-100 flex-xxl-row">
            <!-- Chessboard -->
            <div class="w-100 w-xxl-75">
              <TheChessboard
                :board-config="boardConfig"
                reactive-config
                @board-created="handleBoardCreated"
                @move="handleMove"
              />
            </div>

            <!-- Sidebar -->
            <div class="w-100 w-xxl-25 rounded p-4 ms-xxl-4 sidebar d-flex flex-column max-width-xxl bg-dark">
              <div>
                <div class="d-flex flex-column bgr-primary p-3 top-bar clr-b">
                <h6 class="custom-text text-center">
                  {{blunderData.opening}}
                </h6>
                </div>
                <h1 class="text-center">Blunder Review</h1>
                <div class="border-custom mb-4"></div>
                <div v-if="moveFeedback.visible">
                  <!-- If it's a blunder -->
                  <div v-if="moveFeedback.type === 'blunder'" class="mb-5">
                    <h4 class="p-1">
                      <img :src="blunderIcon" alt="Chess Blunder Icon" class="customIcon"/>
                      {{ moveFeedback.moveText }} was a blunder!
                    </h4>
                  </div>

                  <!-- If it's a good move -->
                  <div v-else-if="moveFeedback.type === 'good'" class="mb-5">
                    <h4 class="p-1">
                      <img :src="correctIcon" alt="Chess Correct Icon" class="customIcon"/>
                      {{ moveFeedback.moveText }} is good!
                    </h4>
                  </div>
                  <div v-else-if="moveFeedback.type === 'best'" class="mb-5">
                    <h4 class="p-1">
                      <img :src="bestIcon" alt="Chess Best Icon" class="customIcon"/>
                      {{ moveFeedback.moveText }} is the best move!
                    </h4>
                  </div>
                </div>
              </div>

              <div class="mt-auto d-flex flex-column bgr-primary bottom-bar">
                <div v-if="moveFeedback.type === 'blunder'">
                  <div class="d-flex w-100">
                    <button @click="handleRetry" class="w-50 btn-custom">
                    <h6 class="m-2"><font-awesome-icon :icon="['fas', 'rotate-right']" /> RETRY</h6>
                    </button>
                    <button @click="handleBestMove()" class="w-50 btn-custom">
                      <h6 class="m-2"><font-awesome-icon :icon="['fas', 'lightbulb']" /> SHOW BEST MOVE</h6>
                    </button>
                  </div>
                </div>
                <div v-else-if="moveFeedback.type === 'best' || moveFeedback.type === 'good'">
                  <div class="d-flex w-100">
                    <button @click="openLichessPosition" class="btn-custom w-50">
                      <h6 class="m-2"> <font-awesome-icon :icon="['fas', 'magnifying-glass']" /> </h6>
                    </button>
                    <button @click="nextBlunder" class="w-50 btn-custom">
                      <h6 class="m-2"> <font-awesome-icon :icon="['fas', 'arrow-right']" /> </h6>
                    </button>
                  </div>
                </div>
                <div v-else>
                  <div class="p-4 clr-b">
                    <h4 class="m-1 text-center">  PLAY A MOVE WITH {{ blunderData.playing_side }} </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>

.navbar-container {
    background: transparent; 
    font-size: 2rem;
    font-family: 'Oswald'; 
  }

 .search {
    color: #ebecd0; 
  }

 .analysis {
    color: #ebecd0; 
  }

  
  /* Navbar Items */
  .nav-item {
    cursor: pointer;
    transition: color 0.2s ease-in-out;
  }
  
  .nav-item:hover {
    color: #fff; 
  }

  
.btn-custom {
  background-color: transparent;
  color: #121212;
  padding: 1.5rem;
  border: none;
}

.btn-custom:hover {
  background-color: #c192fc;
  color: #121212;
  border-radius: 0;
}

.customIcon {
  width: 3vh;
  height: 3vh;
}

.custom-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.custom-text:hover {
  text-overflow: clip;
  white-space: normal;
}

.top-bar {
  margin: -1.5rem;
  margin-bottom: 1.5rem;
}

.bottom-bar {
  margin: -1.5rem;
}

.border-custom {
  display: block;
  height: 1px;
  border: 0;
  border-top: 1px solid #ccc;
  margin: 1em 0;
  padding: 0;
}

.clr-w {
  color: #EBECD0;
}

.clr-b {
  color: #121212;
}

.bgr-primary {
  background-color: #BB86FC;
}

.bgr-secondary {
  background-color: #2e2e2e;
}

.btn-outline-success {
  border: none;
}


.btn-outline-danger {
  border: none;
}


.timer {
  font-size: 3.5vh;
}

.card-title {
  font-size: 1.5vh;
}

.card-text {
  font-size: 1vh;
}

.card-max {
  font-size: 2vh;
}

.hintButtons {
  background-color: transparent;
  color: #EBECD0;
}

:deep(.toggle-off) {
  background-color: #EBECD0;
  border-color: #EBECD0;
  outline: none;
}

:deep(.toggle-on) {
  background-color: #BB86FC;
  border-color: #BB86FC;
  outline: none;
}

:deep(.toggle-container, .toggle-container:focus, .toggle-container:active, .toggle-container::after, .toggle-container::before) {
  outline: none;
  border: none;
}


.bgr-dark {
  background-color: #1d1d1d;
}

.sidebar {
  color: #EBECD0;
  min-width: 40vh;
  margin-bottom: 0.5rem;
}

@media (min-width: 1400px) { 
  .max-width-xxl {
    max-width: 40vh;
  }
}

:deep(.main-board) {
  position: relative;
  display: block;
  height: 0;
  padding-bottom: 100%;
  width: 100%;
  cursor: pointer;
}

:deep(cg-board) {
  background-image: url('@/assets/chessboard.svg') ;
  background-size: cover;
  box-shadow: 0 0 20px #BB86FC;
}

:deep(.orientation-white .files coord:nth-child(2n + 1)),
:deep(.orientation-white .ranks coord:nth-child(2n)),
:deep(.orientation-black .files coord:nth-child(2n)),
:deep(.orientation-black .ranks coord:nth-child(2n + 1)) {
  color: #EBECD0 ;
  font-family: 'Roboto', sans-serif ;
}

:deep(.orientation-white .files coord:nth-child(2n)),
:deep(.orientation-white .ranks coord:nth-child(2n + 1)),
:deep(.orientation-black .files coord:nth-child(2n + 1)),
:deep(.orientation-black .ranks coord:nth-child(2n)) {
  color: #BB86FC;
  font-family: 'Roboto', sans-serif ;
}

:deep(cg-board square.last-move) {
  will-change: transform;
  background-color: rgba(35, 2, 75, 0.3);
}
:deep(cg-board square.selected) {
  background-color: rgba(35, 2, 75, 0.3);
}

:deep(cg-board piece.dragging) {
  cursor: move;
}

:deep(cg-board square.move-dest) {
  background: radial-gradient(
    rgba(35, 2, 75, 0.4) 22%,
    rgb(56, 77, 65) 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}

:deep(cg-board square.premove-dest) {
  background: radial-gradient(
    rgba(20, 30, 85, 0.7) 22%,
    #203085 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}

:deep(cg-board square.oc.move-dest) {
  background: radial-gradient(
    transparent 0%,
    transparent 80%,
    rgba(35, 2, 75, 0.4) 80%
  );
}

:deep(cg-board .cg-square.move-dest.drag-over),
:deep(cg-board .cg-square.premove-dest.drag-over) {
  box-shadow: inset 0 0 10px 2px rgba(216, 85, 0, 0.9);
}

:deep(.cg-wrap) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important; 
  height: 100% !important;
}


@media (orientation: landscape) {
  .main-wrap {
    max-width: 100%; /* or whatever new max you prefer */
  }
}

</style>